import type { ReactNode } from 'react';
import { initials, isNoPosition, isPerson, isVacancy } from '../lib/display';

import type { DirectoryHit } from '../lib/types';

export interface PersonTarget {
  role: string;
  name: string | null;
  phone: string | null;
  /**
   * The person's OWN mailbox, from `Facility_Staff` (written there by the
   * Power App's Microsoft 365 people picker). Null for every role that still
   * comes from `Facility_Information`, which stores names only.
   */
  email: string | null;
  /** The SITE inbox — the fallback destination for contacting a facility. */
  siteEmail: string | null;
  /**
   * What the Entra directory made of `name`. Undefined where the caller has no
   * directory to consult; `unknown`/`ambiguous` where it consulted one and could
   * not answer. All three keep the Teams actions off — they are never guessed.
   */
  dir?: DirectoryHit;
  /**
   * The `Facility_Staff` slot (`PropertyManager01`, `Assistant02`…) for a
   * person from the on-site team — what the PunchWISE clock status is keyed
   * by. Absent for every other role, and for a role's "None"/"Unassigned"
   * placeholder card, which is nobody.
   */
  slot?: string;
}

/**
 * Every person row across all four people panels is a button.
 *
 * Finding the manager's number used to be the end of the journey; here it is
 * one tap from reaching them. A vacancy gets an amber avatar and amber text so
 * vacancies are countable at a glance rather than reading as someone's name.
 */
export function Person({
  target,
  onOpen,
  aside,
}: {
  target: PersonTarget;
  onOpen: (t: PersonTarget) => void;
  /** Drawn on the right of the row, before the chevron — the clock status. */
  aside?: ReactNode;
}) {
  const { role, name, phone } = target;
  const real = isPerson(name);
  const hiring = isVacancy(name);
  const none = isNoPosition(name);
  const ini = real ? initials(name!) : null;

  return (
    <button className="person" onClick={() => onOpen(target)} type="button">
      <div className={`p-av${hiring ? ' hiring' : ''}${ini ? '' : ' none'}`}>
        {ini || (hiring ? '!' : '—')}
      </div>
      <div className="p-b">
        <div className="p-role">{role}</div>
        <div className={`p-name${hiring ? ' hiring' : ''}`}>
          {real ? (
            name
          ) : hiring ? (
            'Now Hiring'
          ) : (
            // 'None' is the DM recording that this position does not exist;
            // an empty slot is nobody having said anything. Different claims.
            <span className="na">{none ? 'None' : 'Unassigned'}</span>
          )}
        </div>
        {phone && <div className="p-ph">{phone}</div>}
      </div>
      {aside}
      <span className="p-go" aria-hidden="true">
        ›
      </span>
    </button>
  );
}
